/**
 * Maps a speaker label to a stable colour token from the speaker palette.
 *
 * The palette lives in `packages/core-ui/src/globals.css` as
 * `--speaker-color-1` through `--speaker-color-6`. We expose only the token
 * names here — consumers receive a `var(--speaker-color-N)` reference so the
 * renderer can pick the right colour for the active theme.
 *
 * Behaviour:
 * - `"Speaker 0"` → token 1, `"Speaker 1"` → token 2, … (zero-based, +1 to
 *   align with the human-readable token name).
 * - Free-form names ("Alice", "bruno@…") → deterministic hash into the
 *   palette so the same name always renders in the same colour.
 * - Empty / undefined labels fall back to the first token.
 *
 * Verified against globals.css before adding tokens: no `--speaker-color-*`
 * existed previously, so the 6 new tokens introduced are net-additive.
 */

export const SPEAKER_COLOR_TOKENS = [
  "speaker-color-1",
  "speaker-color-2",
  "speaker-color-3",
  "speaker-color-4",
  "speaker-color-5",
  "speaker-color-6",
] as const;

const PALETTE_SIZE = SPEAKER_COLOR_TOKENS.length;

const SPEAKER_PREFIX = /^speaker\s+(\d+)$/i;

/**
 * Returns a `var(--speaker-color-N)` reference for the given label.
 * Deterministic — same label always returns the same token reference.
 */
export function speakerColor(label: string | undefined | null): string {
  if (!label) {
    return tokenRef(0);
  }

  // Common case: "Speaker N" — slot the number directly into the palette.
  const m = label.match(SPEAKER_PREFIX);
  if (m) {
    const n = Number.parseInt(m[1], 10);
    if (Number.isFinite(n) && n >= 0) {
      return tokenRef(n % PALETTE_SIZE);
    }
  }

  // Fallback: hash the label into the palette so free-form names are stable.
  let hash = 0;
  for (let i = 0; i < label.length; i += 1) {
    // djb2-style — small, deterministic, no deps.
    hash = (hash * 33 + label.charCodeAt(i)) >>> 0;
  }
  return tokenRef(hash % PALETTE_SIZE);
}

function tokenRef(index: number): string {
  return `var(--${SPEAKER_COLOR_TOKENS[index]})`;
}
